import { MouseEvent, ReactNode } from "react";
import { cn } from "@volca/ui/lib/utils";
import { useEnv } from "@/core/env";
import { ServiceTitanMark } from "./service-titan-mark";

interface ServiceTitanIconLinkProps {
  url: string;
  label?: string;
  stopPropagation?: boolean;
  /**
   * "button" renders a padded link with muted resting text and a hover
   * background; font size is inherited from context. Omitted, the link
   * renders bare, with no padding or hover background.
   */
  variant?: "button";
  className?: string;
  children?: ReactNode;
}

export const ServiceTitanIconLink = ({
  url,
  label,
  stopPropagation = false,
  variant,
  className,
  children,
}: ServiceTitanIconLinkProps) => {
  const { serviceTitanBaseUrl } = useEnv();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  return (
    <a
      href={serviceTitanBaseUrl + url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      onClick={handleClick}
      className={cn(
        "group inline-flex shrink-0 items-center gap-1 rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-focus/50",
        {
          "gap-1.5 rounded-md px-1.5 py-1 text-subtle transition-colors hover:bg-neutral-subtle-hovered hover:text-ink":
            variant === "button",
          "text-link": !variant && !!children,
          "-my-1 ml-0.5 p-1 hover:bg-neutral-hovered": !variant && !children,
        },
        className,
      )}
    >
      <ServiceTitanMark />
      {children && (
        <span
          className={cn({
            "underline decoration-link/40 underline-offset-2 group-hover:decoration-link":
              !variant,
          })}
        >
          {children}
        </span>
      )}
    </a>
  );
};
